/**
 * AMD-51 — Anomalies. Named, timed, one-at-a-time world mutations on a seeded schedule.
 *
 * Framework borrowed from kimi-test-matt's events registry (rarity / danger / minWave
 * metadata; adding a def means appending to `ANOMALIES`, nothing else); implementation
 * native to this sim. Scheduling draws come only from the `anomaly` stream, so spawn /
 * loot / ai / crit determinism is untouched by anomaly timing.
 *
 * Wave gates ride AMD-34: `currentWave` is a pure function of the tick, so eligibility is
 * too, and a resumed run re-derives the same calendar by fast-forwarding the stream past
 * every window the saved tick already crossed.
 */

import type { RngStream } from '../core/rng';
import { currentWave } from './director';
import { HAZARD_VENT, spawnHazard } from './hazards';
import { wrapDeltaX, wrapDeltaY, wrapX, wrapY, WRAP_HALF_H, WRAP_HALF_W } from './wrap';
import type { World } from './world';

export type AnomalyRarity = 'common' | 'uncommon' | 'rare';

/** Scheduling weights — commons fill the calendar, rares stay special. */
const RARITY_WEIGHT: Readonly<Record<AnomalyRarity, number>> = { common: 100, uncommon: 45, rare: 15 };

export interface AnomalyDef {
  id: string;
  /** Display name for the event log (and an eventual HUD chip — AMD-51 "not done"). */
  name: string;
  /** 1..3 warning level. */
  danger: number;
  rarity: AnomalyRarity;
  /** Not schedulable before this wave (waves are pure functions of the tick, AMD-34). */
  minWave: number;
  durationTicks: number;
  /** `rng` is the anomaly stream — the only stream this system may draw from. */
  start(w: World, rng: RngStream): void;
  update(w: World, rng: RngStream, progress: number): void;
  end(w: World): void;
}

// --- v1 defs ---

/** Vents erupt at seeded offsets around the player, through the standard §10 telegraph. */
const VENT_COUNT = 8;
const VENT_RING_U = 7;

/** §8 accrual multiplier while swarm-surge runs. The budget table itself never moves. */
const SURGE_THREAT_SCALE = 1.6;

/** Undertow displacement, u/s — positional like §7.3 separation, composes with every mover. */
const UNDERTOW_U_PER_S = 1.8;

export const ANOMALIES: readonly AnomalyDef[] = [
  {
    id: 'anomaly.vent-storm',
    name: 'VENT STORM',
    danger: 1,
    rarity: 'common',
    minWave: 3,
    durationTicks: 20 * 60,
    start() {
      /* vents spawn across the window, not on the first tick */
    },
    update(w, rng, progress) {
      // One vent per 1/VENT_COUNT of the window, on the tick its slice begins.
      const slice = Math.floor(progress * VENT_COUNT);
      const st = runtime(w);
      if (slice < st.ventsSpawned || slice >= VENT_COUNT) return;
      st.ventsSpawned = slice + 1;
      const ang = rng.next() * Math.PI * 2;
      const dist = VENT_RING_U * (0.5 + rng.next() * 0.5);
      spawnHazard(
        w,
        HAZARD_VENT,
        w.player.pos.x + Math.cos(ang) * dist,
        w.player.pos.y + Math.sin(ang) * dist,
        'circle',
        1.6,
        1.5,
        2.5,
      );
    },
    end() {
      /* spawned vents run out their own §10 lifetimes */
    },
  },
  {
    id: 'anomaly.swarm-surge',
    name: 'SWARM SURGE',
    danger: 1,
    rarity: 'common',
    minWave: 4,
    durationTicks: 30 * 60,
    start(w) {
      w.anomalyThreatScale = SURGE_THREAT_SCALE;
    },
    update() {
      /* the scale does the work */
    },
    end(w) {
      w.anomalyThreatScale = 1;
    },
  },
  {
    id: 'anomaly.undertow',
    name: 'UNDERTOW',
    danger: 2,
    rarity: 'uncommon',
    minWave: 6,
    durationTicks: 18 * 60,
    start(w, rng) {
      const st = runtime(w);
      st.sinkX = wrapX((rng.next() * 2 - 1) * WRAP_HALF_W);
      st.sinkY = wrapY((rng.next() * 2 - 1) * WRAP_HALF_H);
    },
    update(w) {
      const st = runtime(w);
      const step = UNDERTOW_U_PER_S / 60;
      drift(w, w.player.pos, st.sinkX, st.sinkY, step);
      const list = w.enemies.liveIndices();
      for (let i = 0; i < list.length; i++) {
        const e = w.enemies.slots[list[i]];
        drift(w, e.pos, st.sinkX, st.sinkY, step);
      }
    },
    end() {
      /* the current simply stops */
    },
  },
];

/** Toroidal pull of `step` u toward (tx, ty). No-op inside the dead zone around the sink. */
function drift(w: World, pos: { x: number; y: number }, tx: number, ty: number, step: number): void {
  const dx = wrapDeltaX(tx - pos.x);
  const dy = wrapDeltaY(ty - pos.y);
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d < 1) return;
  pos.x = wrapX(pos.x + (dx / d) * step);
  pos.y = wrapY(pos.y + (dy / d) * step);
}

// --- scheduler ---

/** First window opens 90–150 s in; every later one 120–180 s after the previous close. */
const FIRST_MIN_TICKS = 90 * 60;
const FIRST_MAX_TICKS = 150 * 60;
const GAP_MIN_TICKS = 120 * 60;
const GAP_MAX_TICKS = 180 * 60;

interface AnomalyRuntime {
  initialized: boolean;
  nextStartTick: number;
  active: AnomalyDef | null;
  activeStartTick: number;
  ventsSpawned: number;
  sinkX: number;
  sinkY: number;
}

const RUNTIME = new WeakMap<World, AnomalyRuntime>();

function runtime(w: World): AnomalyRuntime {
  let st = RUNTIME.get(w);
  if (st === undefined) {
    st = { initialized: false, nextStartTick: 0, active: null, activeStartTick: 0, ventsSpawned: 0, sinkX: 0, sinkY: 0 };
    RUNTIME.set(w, st);
  }
  return st;
}

function rollGap(rng: RngStream, min: number, max: number): number {
  return min + Math.floor(rng.next() * (max - min + 1));
}

/** Rarity-weighted roll over defs whose wave gate the clock has passed. Null when none is. */
function pick(w: World, rng: RngStream): AnomalyDef | null {
  const wave = currentWave(w);
  let total = 0;
  for (let i = 0; i < ANOMALIES.length; i++) if (ANOMALIES[i].minWave <= wave) total += RARITY_WEIGHT[ANOMALIES[i].rarity];
  // The roll is drawn even when nothing is eligible, so the stream advances identically
  // whether wave gates pass or not — a resume that fast-forwards draws stays aligned.
  let roll = rng.next() * total;
  if (total === 0) return null;
  for (let i = 0; i < ANOMALIES.length; i++) {
    const def = ANOMALIES[i];
    if (def.minWave > wave) continue;
    roll -= RARITY_WEIGHT[def.rarity];
    if (roll < 0) return def;
  }
  return null;
}

/** Registered as `anomalies`, between `player` and `director` (AMD-51 step order). */
export function stepAnomalies(w: World): void {
  const st = runtime(w);
  const rng = w.rng.anomaly;

  if (!st.initialized) {
    st.initialized = true;
    st.nextStartTick = rollGap(rng, FIRST_MIN_TICKS, FIRST_MAX_TICKS);
    // Resume path: every window the saved tick already crossed is spent, not replayed —
    // the same rule the run snapshot applies to the swarm on screen.
    while (st.nextStartTick <= w.tick) {
      pick(w, rng);
      st.nextStartTick += rollGap(rng, GAP_MIN_TICKS, GAP_MAX_TICKS);
    }
  }

  if (st.active !== null) {
    const t = w.tick - st.activeStartTick;
    const def = st.active;
    if (t >= def.durationTicks) {
      def.end(w);
      w.bus.emit('anomaly', { id: def.id, name: def.name, danger: def.danger, phase: 'end' });
      st.active = null;
      st.nextStartTick = w.tick + rollGap(rng, GAP_MIN_TICKS, GAP_MAX_TICKS);
    } else {
      def.update(w, rng, t / def.durationTicks);
    }
    return;
  }

  if (w.tick >= st.nextStartTick) {
    const def = pick(w, rng);
    if (def === null) {
      // Nothing eligible yet: the draw happened (stream aligned); retry next window.
      st.nextStartTick = w.tick + rollGap(rng, GAP_MIN_TICKS, GAP_MAX_TICKS);
      return;
    }
    st.active = def;
    st.activeStartTick = w.tick;
    st.ventsSpawned = 0;
    def.start(w, rng);
    w.bus.emit('anomaly', { id: def.id, name: def.name, danger: def.danger, phase: 'start' });
  }
}

/** The anomaly running right now, for surfaces that want to say so. Null between windows. */
export function activeAnomaly(w: World): { id: string; name: string; danger: number; progress: number } | null {
  const st = RUNTIME.get(w);
  if (st === undefined || st.active === null) return null;
  const def = st.active;
  return { id: def.id, name: def.name, danger: def.danger, progress: (w.tick - st.activeStartTick) / def.durationTicks };
}
